<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header con breadcrumb -->
    <div class="bg-white border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <div class="flex items-center space-x-4">
            <button @click="goBack" class="flex items-center text-gray-500 hover:text-gray-700">
              <svg class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
              Volver
            </button>
            <nav class="flex" aria-label="Breadcrumb">
              <ol class="flex items-center space-x-2">
                <li>
                  <RouterLink to="/workspace" class="text-gray-500 hover:text-gray-700">Workspace</RouterLink>
                </li>
                <li>
                  <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                  </svg>
                </li>
                <li>
                  <span class="text-gray-500">Tickets</span>
                </li>
                <li>
                  <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                  </svg>
                </li>
                <li>
                  <span class="text-gray-900 font-medium">#{{ ticketId }}</span>
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div v-if="ticket" class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Columna Principal (Timeline y Header) -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Header del Ticket -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1">
                <h1 class="text-2xl font-bold text-gray-900 mb-2">{{ ticket.title }}</h1>
                <p class="text-gray-600 leading-relaxed">{{ ticket.description }}</p>
              </div>
              
              <div class="flex items-center space-x-2 ml-4">
                <span :class="statusInfo.color" class="px-3 py-1 rounded-full text-sm font-medium">
                  {{ statusInfo.label }}
                </span>
              </div>
            </div>
            
            <!-- Meta información rápida -->
            <div class="flex items-center space-x-6 text-sm text-gray-500">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Creado {{ formatTimestamp(ticket.createdAt) }}
              </div>
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                {{ ticket.client.name }}
              </div>
              <div class="flex items-center">
                <span :class="priorityInfo.color" class="w-2 h-2 rounded-full mr-2"></span>
                Prioridad {{ priorityInfo.label }}
              </div>
            </div>
          </div>

          <!-- Timeline de Mensajes -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200">
            <div class="border-b border-gray-200 p-6">
              <h2 class="text-lg font-semibold text-gray-900">Conversación</h2>
              <p class="text-sm text-gray-500 mt-1">Historial de mensajes y actualizaciones</p>
            </div>
            
            <!-- Contenedor principal con altura fija más grande -->
            <div class="flex flex-col" style="height: 600px;">
              <!-- Área de mensajes con scroll independiente -->
              <div ref="messagesContainer" class="flex-1 p-6 space-y-6 overflow-y-auto" style="max-height: calc(100% - 140px);">
                <div v-for="message in messages" :key="message.id" class="relative">
                  <!-- Mensaje del Cliente -->
                  <div v-if="message.type === 'client'" class="flex justify-end">
                    <div class="flex items-start space-x-2 max-w-2xl">
                      <div class="bg-ecomac-primary text-white rounded-lg px-4 py-3 shadow-sm">
                        <div v-if="message.prefix" class="text-xs text-ecomac-light mb-1 opacity-80">
                          {{ message.prefix }}
                        </div>
                        <p class="text-sm">{{ message.content }}</p>
                        <div class="text-xs text-ecomac-light mt-2 opacity-80">
                          {{ formatDate(message.timestamp) }}
                        </div>
                      </div>
                      <div class="w-8 h-8 bg-ecomac-primary rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {{ message.author.avatar }}
                      </div>
                    </div>
                  </div>

                  <!-- Mensaje del Soporte -->
                  <div v-else-if="message.type === 'support'" class="flex justify-start">
                    <div class="flex items-start space-x-2 max-w-2xl">
                      <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {{ message.author.avatar }}
                      </div>
                      <div class="bg-gray-100 rounded-lg px-4 py-3 shadow-sm">
                        <div class="text-xs text-gray-600 mb-1 font-medium">
                          {{ message.author.name }} - {{ message.author.role }}
                        </div>
                        <p class="text-sm text-gray-900">{{ message.content }}</p>
                        <div class="text-xs text-gray-500 mt-2">
                          {{ formatDate(message.timestamp) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Mensaje del Sistema -->
                  <div v-else-if="message.type === 'system'" class="flex justify-center">
                    <div class="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 max-w-md">
                      <div class="flex items-center">
                        <svg class="h-4 w-4 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <p class="text-sm text-yellow-800">{{ message.content }}</p>
                      </div>
                      <div class="text-xs text-yellow-600 mt-1 text-center">
                        {{ formatDate(message.timestamp) }}
                      </div>
                    </div>
                  </div>

                  <!-- Nota Interna (solo visible para el equipo) -->
                  <div v-else-if="message.type === 'internal' && showInternalMessages" class="flex justify-start">
                    <div class="flex items-start space-x-2 max-w-2xl">
                      <div class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {{ message.author.avatar }}
                      </div>
                      <div class="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
                        <div class="flex items-center mb-1">
                          <svg class="h-4 w-4 text-purple-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                          </svg>
                          <span class="text-xs text-purple-700 font-medium">Nota Interna</span>
                        </div>
                        <p class="text-sm text-purple-900">{{ message.content }}</p>
                        <div class="text-xs text-purple-600 mt-2">
                          {{ message.author.name }} - {{ formatDate(message.timestamp) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Input para nuevo mensaje fijo en la parte inferior -->
              <div class="border-t border-gray-200 p-6 bg-white">
                <div class="flex items-start gap-3">
                  <div class="flex-1">
                    <textarea
                      v-model="newMessage"
                      @keydown.enter.prevent="sendMessage"
                      class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ecomac-primary focus:border-transparent resize-none text-sm"
                      placeholder="Escribe tu mensaje aquí..."
                      rows="3"
                    ></textarea>
                    <p class="text-xs text-gray-500 mt-2">Los mensajes se enviarán con el prefijo "[Portal Cliente]"</p>
                  </div>
                  <button
                    @click="sendMessage"
                    :disabled="!newMessage.trim()"
                    class="btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px] h-[84px] rounded-lg"
                  >
                    <div class="flex flex-col items-center">
                      <svg class="h-5 w-5 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                      </svg>
                      <span class="text-sm font-medium">Enviar</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Columna Lateral (Información del Ticket) -->
        <div class="lg:col-span-1">
          <TicketInfo :ticket="ticket" @edit-ticket="openEditModal" />
        </div>
      </div>
      
      <!-- Loading State -->
      <div v-else class="flex items-center justify-center h-64">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-ecomac-primary mx-auto"></div>
          <p class="text-gray-500 mt-4">Cargando ticket...</p>
        </div>
      </div>
    </div>
    
    <!-- Modal de Edición -->
    <TicketEditModal
      v-model="showEditModal"
      :ticket="ticket"
      @save="handleTicketSaved"
      @cancel="handleEditCancelled"
    />
  </div>
</template>
<script setup>
import { computed, ref, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TicketInfo from '../components/TicketInfo.vue';
import TicketEditModal from '../components/TicketEditModal.vue';
// TODO: Reemplazar con servicios reales cuando estén disponibles
function getStatusInfo(status) {
  const statusMap = {
    'abierto': { label: 'Abierto', color: 'bg-red-100 text-red-800', badgeColor: 'bg-red-500' },
    'en_progreso': { label: 'En Progreso', color: 'bg-yellow-100 text-yellow-800', badgeColor: 'bg-yellow-500' },
    'resuelto': { label: 'Resuelto', color: 'bg-green-100 text-green-800', badgeColor: 'bg-green-500' },
    'cerrado': { label: 'Cerrado', color: 'bg-gray-100 text-gray-800', badgeColor: 'bg-gray-500' },
    // Mantener compatibilidad con estados en inglés por si acaso
    'open': { label: 'Abierto', color: 'bg-red-100 text-red-800', badgeColor: 'bg-red-500' },
    'in_progress': { label: 'En Progreso', color: 'bg-yellow-100 text-yellow-800', badgeColor: 'bg-yellow-500' },
    'resolved': { label: 'Resuelto', color: 'bg-green-100 text-green-800', badgeColor: 'bg-green-500' },
    'closed': { label: 'Cerrado', color: 'bg-gray-100 text-gray-800', badgeColor: 'bg-gray-500' }
  };
  return statusMap[status] || statusMap['abierto'];
}

function getPriorityInfo(priority) {
  const priorityMap = {
    'baja': { label: 'Baja', color: 'bg-green-500', textColor: 'text-green-700' },
    'media': { label: 'Media', color: 'bg-yellow-500', textColor: 'text-yellow-700' },
    'alta': { label: 'Alta', color: 'bg-red-500', textColor: 'text-red-700' }
  };
  return priorityMap[priority] || priorityMap['media'];
}

function formatDate(timestamp) {
  return new Date(timestamp).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatTimestamp(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) {
    return `Hace ${diffDays} día`;
  } else if (diffDays > 1) {
    return `Hace ${diffDays} días`;
  } else {
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    if (diffHours === 1) {
      return `Hace ${diffHours} hora`;
    } else if (diffHours > 1) {
      return `Hace ${diffHours} horas`;
    } else {
      const diffMinutes = Math.ceil(diffTime / (1000 * 60));
      return `Hace ${diffMinutes} minutos`;
    }
  }
}
import { useAuthStore } from '../store/authStore';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const ticketId = Number(route.params.id || 1);
const ticket = ref(null);
const messages = ref([]);
const newMessage = ref('');
const messagesContainer = ref(null);

// Modal state
const showEditModal = ref(false);

const showInternalMessages = computed(() => {
  // Solo mostrar mensajes internos si el usuario es del equipo (no cliente)
  return !authStore.isClient;
});

// Computed properties
const statusInfo = computed(() => {
  if (!ticket.value) return { label: '', color: '', badgeColor: '' };
  return getStatusInfo(ticket.value.status);
});

const priorityInfo = computed(() => {
  if (!ticket.value) return { label: '', color: '', textColor: '' };
  return getPriorityInfo(ticket.value.priority);
});

// Scroll functions
function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

// Functions
function loadTicketData() {
  // TODO: Cargar ticket real desde API
  console.warn('Carga de tickets no implementada sin datos mock');
  
  // Crear ticket temporal para evitar errores
  ticket.value = {
    id: ticketId,
    title: `Ticket #${ticketId}`,
    description: 'Funcionalidad no disponible sin datos mock',
    status: 'open',
    priority: 'media',
    category: 'General',
    client: { name: 'Usuario', email: 'usuario@ejemplo.com' },
    assignedTo: { name: 'Equipo Soporte' },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  messages.value = [];
  
  // Auto-scroll to bottom when messages load
  scrollToBottom();
}

function sendMessage() {
  if (!newMessage.value.trim()) return;
  
  const newMsg = {
    id: Date.now(),
    ticketId: ticketId,
    type: 'client',
    author: {
      id: authStore.userId || 999,
      name: authStore.userName || 'Usuario',
      avatar: authStore.userName ? authStore.userName.substring(0, 2).toUpperCase() : 'US',
      role: 'Cliente'
    },
    content: newMessage.value.trim(),
    timestamp: new Date().toISOString(),
    isInternal: false,
    prefix: '[Portal Cliente]'
  };
  
  messages.value.push(newMsg);
  newMessage.value = '';
  
  // Auto-scroll to bottom after sending message
  scrollToBottom();
}

function goBack() {
  router.back();
}

// Modal functions
function openEditModal() {
  showEditModal.value = true;
}

function handleTicketSaved(updatedTicket) {
  // Actualizar el ticket local con los datos del servidor
  ticket.value = { ...ticket.value, ...updatedTicket };
  
  // Opcional: recargar toda la data si es necesario
  // loadTicketData();
}

function handleEditCancelled() {
  // El modal ya se cierra automáticamente, no necesitamos hacer nada más
}

onMounted(() => {
  loadTicketData();
});
</script>

